import { useState } from 'react';
import { GlassCard } from '@/components/ui/GlassCard';
import { TextureButton } from '@/components/ui/TextureButton';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Lock, Mail } from 'lucide-react';

export function AuthPage() {
    const [mode, setMode] = useState<'login' | 'signup' | 'landing'>('landing');
    const { login, signup, loginWithGoogle, enterGuestMode, manuallyHydrate } = useAuthStore();
    const navigate = useNavigate();

    // Form State
    const [email, setEmail] = useState('');
    const [pin, setPin] = useState('');
    const [dob, setDob] = useState('');
    const [error, setError] = useState('');
    const [role, setRole] = useState<'athlete' | 'parent' | 'coach'>('athlete');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const res = await login(email, pin);
        if (res.success) navigate('/');
        else setError(res.error || 'Login failed');
    };

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (dob && role === 'athlete') {
            const birthDate = new Date(dob);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;

            if (age < 13) {
                setError('Registration Restricted: Under COPPA regulations, athletes under 13 cannot create independent accounts. Please have a parent or guardian create an account with the "Parent" role to manage your profile.');
                return;
            }
        }

        const res = await signup(email, pin, role);
        if (res.success) {
            alert('Signup successful! Check your email to confirm your account creation.');
            setMode('login');
        } else {
            setError(res.error || 'Signup failed');
        }
    };

    const handleGoogle = async () => {
        // Force the redirect to be the current site origin, NOT localhost
        const redirectUrl = window.location.origin;
        console.log('Google Auth starting. Redirecting to:', redirectUrl);

        const res = await loginWithGoogle(mode === 'signup' ? role : undefined);
        if (res.success) navigate('/');
        else setError(res.error || 'Google Auth Failed');
    };

    /* ─── Render: Landing ────────────────────────────────────────── */
    if (mode === 'landing') {
        return (
            <div className="min-h-screen bg-bg relative overflow-hidden flex flex-col items-center justify-end pb-12 px-6">
                {/* Dynamic Background */}
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?q=80&w=2069&auto=format&fit=crop')] bg-cover bg-center opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/80 to-transparent" />

                <div className="relative z-10 w-full max-w-sm space-y-6 animate-fade-in-up">
                    <div className="text-center space-y-2 mb-10 flex flex-col items-center">
                        <img
                            src="/logo.png"
                            alt="Palaestra Logo"
                            className="w-24 h-24 mb-4 drop-shadow-[0_0_15px_rgba(201,162,39,0.3)] animate-float"
                        />
                        <h1 className="text-5xl font-black text-white tracking-tighter drop-shadow-lg">
                            PALAESTRA
                        </h1>
                        <p className="text-gold-500 font-bold tracking-widest uppercase text-xs">
                            Elite Wrestling Training
                        </p>
                    </div>

                    <TextureButton onClick={() => setMode('signup')} className="w-full">
                        Create Account
                    </TextureButton>

                    <button
                        onClick={() => setMode('login')}
                        className="w-full py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold tracking-wide hover:bg-white/10 transition-colors"
                    >
                        Sign In
                    </button>

                    <button
                        type="button"
                        onClick={handleGoogle}
                        className="w-full flex items-center justify-center gap-2 bg-white text-black font-bold py-3 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                        <span className="text-sm">Continue with Google</span>
                    </button>

                    <div className="text-center px-4">
                        <p className="text-[10px] text-text-muted leading-tight">
                            By continuing, you certify that you are 13 years of age or older, or have legal parental/guardian consent to use this application.
                        </p>
                    </div>

                    <button
                        onClick={enterGuestMode}
                        className="w-full py-2 text-text-muted text-[10px] font-bold uppercase tracking-widest hover:text-white transition-colors"
                    >
                        Continue as Guest
                    </button>

                </div>

                {/* Auth Debugger for Landing Page */}
                <div className="relative z-10 w-full max-w-sm mt-8 pt-4 border-t border-white/5 opacity-40">
                    <div className="bg-black/60 rounded-xl p-3 font-mono text-[9px] text-left space-y-2">
                        <p className="text-gold-500/70 font-bold uppercase text-center mb-1">Emergency Tools</p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={async () => {
                                    const url = prompt('Paste the full URL from the "localhost:3000" error page:');
                                    if (url) {
                                        const res = await manuallyHydrate(url);
                                        if (res.success) { alert('Success!'); navigate('/'); }
                                        else alert('Error: ' + res.error);
                                    }
                                }}
                                className="px-2 py-2 bg-gold-500/20 border border-gold-500/30 rounded-lg text-gold-500 font-bold text-center"
                            >
                                Paste Login URL
                            </button>
                            <button
                                onClick={() => {
                                    if ('serviceWorker' in navigator) {
                                        navigator.serviceWorker.getRegistrations().then(regs => {
                                            for (let r of regs) r.unregister();
                                            window.location.reload();
                                        });
                                    } else { window.location.reload(); }
                                }}
                                className="px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-text-muted font-bold text-center"
                            >
                                Hard Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ─── Render: Form ───────────────────────────────────────────── */
    return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-6 relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,162,39,0.05)_0%,transparent_70%)]" />

            <GlassCard className="w-full max-w-sm p-8 relative z-10">
                <div className="text-center mb-8 flex flex-col items-center">
                    <img
                        src="/logo.png"
                        alt="Palaestra Logo"
                        className="w-16 h-16 mb-4 drop-shadow-[0_0_10px_rgba(201,162,39,0.2)]"
                    />
                    <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                        {mode === 'login' ? 'Welcome Back' : 'Join the Ranks'}
                    </h2>
                    <p className="text-text-muted text-xs font-medium mt-2">
                        {mode === 'login' ? 'Enter your credentials to continue.' : 'Begin your journey to mastery.'}
                    </p>

                    {mode === 'signup' && role === 'athlete' && (
                        <div className="mt-4 px-4 py-2 rounded-lg bg-gold-500/5 border border-gold-500/10">
                            <p className="text-[9px] text-gold-400 font-bold uppercase tracking-tight text-center">
                                ⚖️ Age Compliance: Athletes under 13 require parental consent.
                            </p>
                        </div>
                    )}
                </div>

                <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="space-y-4">
                    {/* Role Selection (Signup Only) */}
                    {mode === 'signup' && (
                        <div className="space-y-2 mb-4">
                            <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">I am a...</label>
                            <div className="grid grid-cols-3 gap-2">
                                {(['athlete', 'parent', 'coach'] as const).map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => setRole(r)}
                                        className={`p-2 rounded-lg border text-[10px] font-bold uppercase tracking-wide transition-all ${role === r
                                            ? 'bg-gold-500 text-bg border-gold-500'
                                            : 'bg-white/5 border-white/10 text-text-muted hover:bg-white/10'
                                            }`}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Email</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors"
                                placeholder="name@example.com"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                            <input
                                type="password"
                                value={pin}
                                onChange={e => setPin(e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors"
                                placeholder="6+ chars"
                                required
                            />
                        </div>
                    </div>

                    {mode === 'signup' && (
                        <div className="space-y-1 animate-fade-in">
                            <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Date of Birth</label>
                            <input
                                type="date"
                                value={dob}
                                onChange={e => setDob(e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors appearance-none"
                                required
                            />

                            {/* Underage Notice */}
                            {dob && role === 'athlete' && (() => {
                                const birthDate = new Date(dob);
                                const today = new Date();
                                let age = today.getFullYear() - birthDate.getFullYear();
                                const m = today.getMonth() - birthDate.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;

                                if (age < 13 && age > 0) {
                                    return (
                                        <div className="mt-3 p-3 rounded-lg bg-gold-500/10 border border-gold-500/20 text-gold-200 text-[10px] leading-relaxed animate-pulse">
                                            <p className="font-bold uppercase mb-1 flex items-center gap-1.5">
                                                <AlertCircle className="w-3.5 h-3.5" />
                                                Parent Required
                                            </p>
                                            Athletes under 13 require a parent to set up their account. Please ask your parent to select the <strong>Parent</strong> role above.
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}

                    <div className="text-center px-2">
                        <p className="text-[9px] text-text-muted leading-relaxed">
                            By clicking Create Account or Google, you agree to our Terms of Service and certify you are at least 13 years old or have parental consent.
                        </p>
                    </div>

                    <TextureButton type="submit" className="w-full mt-4">
                        {mode === 'login' ? 'Sign In' : 'Create Account'}
                    </TextureButton>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/10"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-[#0D1117] text-text-muted">Or continue with</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogle}
                        className="w-full flex items-center justify-center gap-2 bg-white text-black font-bold py-3 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                        <span className="text-sm">Google</span>
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                        className="text-xs font-bold text-text-muted hover:text-white transition-colors"
                    >
                        {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                    </button>

                    <div className="mt-4">
                        <button
                            onClick={() => setMode('landing')}
                            className="text-[10px] font-bold text-gold-600 hover:text-gold-500 transition-colors"
                        >
                            Back to Start
                        </button>
                    </div>
                </div>

                {/* Auth Debugger for Mobile Troubleshooting */}
                <div className="mt-8 pt-6 border-t border-white/5 opacity-50">
                    <div className="bg-black/40 rounded-lg p-3 font-mono text-[9px] text-left space-y-1">
                        <p className="text-gold-500/70 font-bold uppercase mb-1">Auth Debugger</p>
                        <p><span className="text-text-muted">Origin:</span> {window.location.origin}</p>
                        <p><span className="text-text-muted">Domain:</span> {window.location.hostname}</p>
                        <button
                            onClick={async () => {
                                try {
                                    const testUrl = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/settings`;
                                    console.log('Testing Supabase Connection:', testUrl);
                                    const res = await fetch(testUrl, {
                                        headers: { 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY }
                                    });
                                    alert(`Supabase Reachable: ${res.status === 200 ? 'YES' : 'ERROR ' + res.status}`);
                                } catch (e: any) {
                                    alert(`Supabase Unreachable: ${e.message}`);
                                }
                            }}
                            className="mt-2 w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-gold-500 hover:bg-white/10 transition-colors text-center"
                        >
                            Verify Supabase Connection
                        </button>
                        <button
                            onClick={async () => {
                                const url = prompt('Paste the full URL from the "localhost:3000" error page here:');
                                if (url) {
                                    const res = await manuallyHydrate(url);
                                    if (res.success) {
                                        alert('Success! Session recovered.');
                                        navigate('/');
                                    } else {
                                        alert('Error: ' + res.error);
                                    }
                                }
                            }}
                            className="mt-2 w-full px-2 py-1 bg-gold-500/20 border border-gold-500/30 rounded text-gold-500 hover:bg-gold-500/30 transition-colors text-center font-bold"
                        >
                            Paste Recovery URL
                        </button>
                    </div>
                </div>
            </GlassCard>
        </div>
    );
}


